/**
 * Analytics utility for tracking user events
 * Supports Google Analytics 4 (GA4)
 * 
 * To set up:
 * 1. Create a GA4 property at https://analytics.google.com
 * 2. Get your Measurement ID (format: G-XXXXXXXXXX)
 * 3. Add it to your .env file as REACT_APP_GA_MEASUREMENT_ID
 */

// Initialize Google Analytics
// Note: If GA script is already loaded in index.html, this will just verify it's ready
export const initGA = () => {
  // Check if gtag is already loaded (from index.html)
  if (typeof window !== 'undefined' && window.gtag) {
    console.log('Google Analytics already initialized from HTML');
    return true;
  }

  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-M5DR27W8W4';
  
  // Load gtag script dynamically if not already loaded
  if (typeof window !== 'undefined' && !window.gtag) {
    const script1 = document.createElement('script');
    script1.async = true;
    script1.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', '${measurementId}', {
        page_path: window.location.pathname,
      });
    `;
    document.head.appendChild(script2);
  }

  return true;
};

// Track page views
export const trackPageView = (path) => {
  if (typeof window !== 'undefined' && window.gtag) {
    const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID || 'G-M5DR27W8W4';
    
    // Extract UTM parameters from URL if present
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {};
    
    if (urlParams.has('utm_source')) utmParams.utm_source = urlParams.get('utm_source');
    if (urlParams.has('utm_medium')) utmParams.utm_medium = urlParams.get('utm_medium');
    if (urlParams.has('utm_campaign')) utmParams.utm_campaign = urlParams.get('utm_campaign');
    
    window.gtag('config', measurementId, {
      page_path: path,
      ...utmParams, // Include UTM parameters in page view
    });
    
    // Also track as event for better visibility
    if (Object.keys(utmParams).length > 0) {
      window.gtag('event', 'page_view_with_utm', {
        ...utmParams,
        page_path: path,
      });
    }
  }
};

// Track custom events
export const trackEvent = (eventName, eventParams = {}) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  } else {
    // Fallback logging for development
    console.log('Analytics Event:', eventName, eventParams);
  }
};

// Predefined event trackers for common actions

/**
 * Track email sign-up
 */
export const trackSignUp = (method = 'email') => {
  trackEvent('sign_up', {
    method: method,
  });
};

/**
 * Track button clicks
 */
export const trackButtonClick = (buttonName, location) => {
  trackEvent('button_click', {
    button_name: buttonName,
    location: location,
  });
};

/**
 * Track scroll depth
 */
export const trackScrollDepth = (depth) => {
  trackEvent('scroll', {
    scroll_depth: depth,
  });
};

/**
 * Track form submission
 */
export const trackFormSubmit = (formName) => {
  trackEvent('form_submit', {
    form_name: formName,
  });
};

/**
 * Track section view (when user scrolls to a section)
 */
export const trackSectionView = (sectionName) => {
  trackEvent('section_view', {
    section_name: sectionName,
  });
};

